import { Room, RoomEvent, Track } from "livekit-client";

const STATES = Object.freeze({
  IDLE: "idle",
  REQUESTING: "requesting_session",
  CAPACITY: "capacity_wait",
  CONNECTING: "connecting_room",
  WAITING: "waiting_for_avatar",
  AVATAR_JOINED: "avatar_joined_begin_transition",
  READY: "ready",
  RECONNECTING: "reconnecting",
  ENDING: "ending",
  ENDED: "ended",
  ERROR: "error",
});

const ALLOWED = Object.freeze({
  idle: [STATES.REQUESTING],
  requesting_session: [STATES.CAPACITY, STATES.CONNECTING, STATES.ENDING, STATES.ERROR],
  capacity_wait: [STATES.CONNECTING, STATES.ENDING, STATES.ERROR],
  connecting_room: [STATES.WAITING, STATES.RECONNECTING, STATES.ENDING, STATES.ERROR],
  waiting_for_avatar: [STATES.AVATAR_JOINED, STATES.RECONNECTING, STATES.ENDING, STATES.ERROR],
  avatar_joined_begin_transition: [STATES.READY, STATES.RECONNECTING, STATES.ENDING, STATES.ERROR],
  ready: [STATES.RECONNECTING, STATES.ENDING, STATES.ERROR],
  reconnecting: [STATES.WAITING, STATES.AVATAR_JOINED, STATES.READY, STATES.ENDING, STATES.ERROR],
  ending: [STATES.ENDED, STATES.ERROR],
  ended: [],
  error: [STATES.ENDING, STATES.ENDED],
});

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
const MAX_CONTROL_MESSAGE_BYTES = 65536;
const STORY_SCHEMA_VERSION = "ensemble-story-0.1";

export function deriveSessionDeadlineMs(offer, now = Date.now()) {
  const expiry = Date.parse(offer.expiresAt || "");
  const seconds = Number(offer.actualDurationSeconds || offer.maxDurationSeconds || 0);
  const duration = Number.isFinite(seconds) && seconds > 0 ? now + seconds * 1000 : NaN;
  const valid = [expiry, duration].filter(Number.isFinite);
  return valid.length ? Math.min(...valid) : null;
}

const TOPICS = Object.freeze({
  CHAT: "ensemble.chat",
  TRANSCRIPT: "ensemble.transcript",
  PERFORMANCE: "ensemble.performance",
  WORLD_STATE: "ensemble.world.state",
  WORLD_EVENT: "ensemble.world.event",
  WORLD_ACTION: "ensemble.world.action",
  SAFETY: "ensemble.safety",
  INTERRUPT: "lk.clear_buffer",
});

const STORY_TOPICS = new Set([
  TOPICS.PERFORMANCE,
  TOPICS.WORLD_STATE,
  TOPICS.WORLD_EVENT,
  TOPICS.SAFETY,
  TOPICS.INTERRUPT,
]);

export function decodeControlMessage(payload) {
  if (!(payload instanceof Uint8Array) || payload.byteLength > MAX_CONTROL_MESSAGE_BYTES) {
    throw new TypeError("Realtime control data must be a Uint8Array no larger than 64 KiB.");
  }
  const value = JSON.parse(new TextDecoder().decode(payload));
  if (!value || Array.isArray(value) || typeof value !== "object") {
    throw new TypeError("Realtime control data must decode to a JSON object.");
  }
  return value;
}

export function validateStoryEnvelope(message, { sessionId, sessionIds, lastSequence = 0 } = {}) {
  if (!message || Array.isArray(message) || typeof message !== "object") {
    throw new TypeError("Story control data must be a JSON object.");
  }
  if (message.schemaVersion !== STORY_SCHEMA_VERSION) {
    throw new TypeError("Story control data uses an unsupported schema.");
  }
  const allowedSessionIds = Array.isArray(sessionIds) ? sessionIds : (sessionId ? [sessionId] : []);
  if (!allowedSessionIds.includes(message.sessionId)) {
    throw new TypeError("Story control data belongs to a different session.");
  }
  if (!Number.isSafeInteger(message.sequence) || message.sequence <= lastSequence) {
    throw new TypeError("Story control data is stale or out of order.");
  }
  if (!Number.isSafeInteger(message.worldRevision) || message.worldRevision < 1) {
    throw new TypeError("Story control data has an invalid world revision.");
  }
  if (!message.payload || Array.isArray(message.payload) || typeof message.payload !== "object") {
    throw new TypeError("Story control data requires an object payload.");
  }
  return message;
}

export class StoryEnvelopeCursor {
  constructor(sessionIds) {
    const requestedIds = (sessionIds || []).map((value) => String(value || "").trim()).filter(Boolean);
    const ids = [...new Set(requestedIds)];
    if (!ids.length || ids.length > 8 || ids.length !== requestedIds.length) {
      throw new TypeError("Story envelope cursor requires one to eight exact session ids.");
    }
    this.sessionIds = Object.freeze(ids);
    this.sequences = new Map(ids.map((id) => [id, 0]));
  }

  accept(message) {
    const sessionId = message?.sessionId;
    const accepted = validateStoryEnvelope(message, {
      sessionIds: this.sessionIds,
      lastSequence: this.sequences.get(sessionId) || 0,
    });
    this.sequences.set(sessionId, accepted.sequence);
    return accepted;
  }

  snapshot() {
    return Object.fromEntries(this.sequences);
  }
}

export class AvatarTrackReadiness {
  constructor(prefixes, primaryPrefix) {
    const requestedPrefixes = (prefixes || []).map((value) => String(value || "").trim()).filter(Boolean);
    const exactPrefixes = [...new Set(requestedPrefixes)];
    const primary = String(primaryPrefix || "").trim();
    if (!exactPrefixes.length || exactPrefixes.length > 8 || exactPrefixes.length !== requestedPrefixes.length
      || !exactPrefixes.includes(primary)) {
      throw new TypeError("Avatar readiness requires one to eight unique prefixes including the primary avatar.");
    }
    if (exactPrefixes.some((prefix) => !prefix.startsWith("face-") || prefix.length > 160)) {
      throw new TypeError("Avatar readiness prefixes must use the reserved face namespace.");
    }
    this.primaryPrefix = primary;
    this.prefixes = Object.freeze(exactPrefixes);
    this.matchOrder = Object.freeze([...exactPrefixes].sort((left, right) => right.length - left.length));
    this.records = new Map(exactPrefixes.map((prefix) => [prefix, {
      participantIdentity: null,
      video: null,
      audio: null,
      audioSignalReceived: false,
      audioDetectionUnavailable: false,
      audioDetection: null,
      audioTimer: null,
    }]));
  }

  match(identity) {
    const value = String(identity || "");
    return this.matchOrder.find((prefix) => value.startsWith(prefix)) || null;
  }

  recordTrack({ prefix, participantIdentity, kind, track }) {
    const record = this.require(prefix);
    const identity = String(participantIdentity || "");
    if (!identity.startsWith(prefix)) throw new TypeError("Avatar track identity is outside its exact prefix.");
    if (record.participantIdentity && record.participantIdentity !== identity) {
      throw new TypeError("More than one participant attempted to own an avatar prefix.");
    }
    if (!["video", "audio"].includes(kind)) throw new TypeError("Avatar track kind must be video or audio.");
    if (record[kind] && record[kind] !== track) {
      throw new TypeError("More than one track attempted to own an avatar media kind.");
    }
    record.participantIdentity = identity;
    record[kind] = track;
    return { prefix, primary: prefix === this.primaryPrefix, record };
  }

  removeTrack(track) {
    for (const prefix of this.prefixes) {
      const record = this.records.get(prefix);
      for (const kind of ["video", "audio"]) {
        if (record[kind] !== track) continue;
        record[kind] = null;
        if (kind === "audio") {
          record.audioSignalReceived = false;
          record.audioDetectionUnavailable = false;
        }
        if (!record.video && !record.audio) record.participantIdentity = null;
        return { prefix, kind, primary: prefix === this.primaryPrefix, record };
      }
    }
    return null;
  }

  markAudioSignal(prefix) {
    const record = this.require(prefix);
    if (!record.audio) throw new TypeError("Avatar audio signal requires an admitted audio track.");
    record.audioSignalReceived = true;
  }

  markDetectionUnavailable(prefix) {
    const record = this.require(prefix);
    if (!record.audio) throw new TypeError("Avatar audio fallback requires an admitted audio track.");
    record.audioDetectionUnavailable = true;
  }

  require(prefix) {
    const record = this.records.get(prefix);
    if (!record) throw new TypeError("Avatar readiness prefix is not admitted to this room.");
    return record;
  }

  get ready() {
    return this.prefixes.every((prefix) => {
      const record = this.records.get(prefix);
      return Boolean(record.video && record.audio
        && (record.audioSignalReceived || record.audioDetectionUnavailable));
    });
  }

  missing() {
    return this.prefixes.flatMap((prefix) => {
      const record = this.records.get(prefix);
      const missing = [];
      if (!record.video) missing.push("video");
      if (!record.audio) missing.push("audio");
      else if (!record.audioSignalReceived && !record.audioDetectionUnavailable) missing.push("audio_signal");
      return missing.map((kind) => ({ prefix, kind }));
    });
  }

  tracks() {
    return this.prefixes.flatMap((prefix) => {
      const record = this.records.get(prefix);
      return [record.video, record.audio].filter(Boolean);
    });
  }

  snapshot() {
    return Object.fromEntries(this.prefixes.map((prefix) => {
      const record = this.records.get(prefix);
      return [prefix, {
        participantIdentity: record.participantIdentity,
        video: Boolean(record.video),
        audio: Boolean(record.audio),
        audioSignalReceived: record.audioSignalReceived,
        audioDetectionUnavailable: record.audioDetectionUnavailable,
        ready: Boolean(record.video && record.audio
          && (record.audioSignalReceived || record.audioDetectionUnavailable)),
      }];
    }));
  }
}

export class EnsembleAvatarStage extends EventTarget {
  constructor({ container, includePrimary = true, interactive = false } = {}) {
    super();
    if (!container || typeof container.append !== "function") {
      throw new TypeError("An Ensemble avatar stage requires a DOM container.");
    }
    this.container = container;
    this.includePrimary = Boolean(includePrimary);
    this.interactive = Boolean(interactive);
    this.selectedCharacterId = null;
    this.controller = null;
    this.records = new Map();
    this.onTrack = (event) => this.attach(event.detail);
    this.onTrackRemoved = (event) => this.remove(event.detail);
  }

  bind(controller) {
    if (!controller || typeof controller.addEventListener !== "function") {
      throw new TypeError("An Ensemble avatar stage requires a realtime room controller.");
    }
    if (this.controller && this.controller !== controller) {
      throw new Error("This avatar stage is already bound to another room controller.");
    }
    if (this.controller === controller) return this;
    this.controller = controller;
    controller.addEventListener("avatartrack", this.onTrack);
    controller.addEventListener("avatartrackremoved", this.onTrackRemoved);
    return this;
  }

  attach(detail = {}) {
    const prefix = String(detail.identityPrefix || "").trim();
    const kind = detail.track?.kind || detail.kind;
    if (!prefix || !["video", "audio"].includes(kind) || !detail.track) {
      throw new TypeError("Avatar stage tracks require an identity prefix, media kind, and track.");
    }
    if (!this.includePrimary && detail.primary) return null;
    const record = this.records.get(prefix) || this.createRecord(prefix, detail);
    if (record.tracks[kind] && record.tracks[kind] !== detail.track) {
      throw new Error("Avatar stage media ownership changed without a removal event.");
    }
    record.tracks[kind] = detail.track;
    const element = kind === "video" ? record.video : record.audio;
    detail.track.attach(element);
    const playback = element.play?.();
    if (playback?.catch) {
      playback.catch((error) => this.dispatchEvent(new CustomEvent("playbackblocked", {
        detail: {
          characterId: record.characterId,
          identityPrefix: prefix,
          kind,
          error: String(error?.message || error),
        },
      })));
    }
    record.node.dataset.media = ["video", "audio"].filter((media) => record.tracks[media]).join("+");
    this.dispatchEvent(new CustomEvent("tilechange", { detail: this.recordSnapshot(record) }));
    return record.node;
  }

  remove(detail = {}) {
    const prefix = String(detail.identityPrefix || "").trim();
    const kind = detail.kind || detail.track?.kind;
    const record = this.records.get(prefix);
    if (!record || !["video", "audio"].includes(kind)) return false;
    if (detail.track && record.tracks[kind] && record.tracks[kind] !== detail.track) return false;
    record.tracks[kind] = null;
    const element = kind === "video" ? record.video : record.audio;
    element.pause?.();
    element.srcObject = null;
    element.removeAttribute("src");
    if (!record.tracks.video && !record.tracks.audio) {
      record.node.remove();
      this.records.delete(prefix);
    } else {
      record.node.dataset.media = ["video", "audio"].filter((media) => record.tracks[media]).join("+");
    }
    this.dispatchEvent(new CustomEvent("tilechange", {
      detail: this.records.has(prefix) ? this.recordSnapshot(record) : {
        characterId: record.characterId,
        identityPrefix: prefix,
        removed: true,
      },
    }));
    return true;
  }

  createRecord(prefix, detail) {
    const roster = this.controller?.avatarRoster?.get(prefix) || {};
    const characterId = String(detail.characterId || roster.characterId || "avatar");
    const node = document.createElement(this.interactive ? "button" : "article");
    node.className = "ensemble-avatar-tile";
    if (this.interactive) {
      node.type = "button";
      node.setAttribute("aria-label", `Speak to ${characterId}`);
      node.addEventListener("click", () => {
        this.selectTarget(characterId);
        this.dispatchEvent(new CustomEvent("tileselect", { detail: this.recordSnapshot(record) }));
      });
    }
    node.dataset.character = characterId;
    node.dataset.identityPrefix = prefix;
    node.dataset.primary = String(Boolean(detail.primary ?? roster.primary));
    node.dataset.media = "";
    const video = document.createElement("video");
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    video.setAttribute("aria-label", `${characterId} live avatar video`);
    const audio = document.createElement("audio");
    audio.autoplay = true;
    audio.setAttribute("aria-label", `${characterId} live avatar audio`);
    const label = document.createElement("span");
    label.className = "ensemble-avatar-label";
    label.textContent = characterId;
    node.append(video, audio, label);
    this.container.append(node);
    const record = { prefix, characterId, node, video, audio, tracks: { video: null, audio: null } };
    this.records.set(prefix, record);
    this.updateSelection(record);
    return record;
  }

  selectTarget(characterId = null) {
    this.selectedCharacterId = characterId === null ? null : String(characterId);
    for (const record of this.records.values()) this.updateSelection(record);
  }

  updateSelection(record) {
    const selected = this.selectedCharacterId === record.characterId;
    record.node.dataset.selected = String(selected);
    if (this.interactive) record.node.setAttribute("aria-pressed", String(selected));
  }

  recordSnapshot(record) {
    return {
      characterId: record.characterId,
      identityPrefix: record.prefix,
      primary: record.node.dataset.primary === "true",
      video: Boolean(record.tracks.video),
      audio: Boolean(record.tracks.audio),
      selected: this.selectedCharacterId === record.characterId,
      removed: false,
    };
  }

  snapshot() {
    return [...this.records.values()].map((record) => this.recordSnapshot(record));
  }

  dispose() {
    this.controller?.removeEventListener("avatartrack", this.onTrack);
    this.controller?.removeEventListener("avatartrackremoved", this.onTrackRemoved);
    for (const record of this.records.values()) record.node.remove();
    this.records.clear();
    this.controller = null;
  }
}

export class EnsembleSessionError extends Error {
  constructor(message, { status = 0, code = "session_request_failed", body = null, retryable = false } = {}) {
    super(message);
    this.name = "EnsembleSessionError";
    this.status = status;
    this.code = code;
    this.body = body;
    this.retryable = retryable;
  }
}

function sessionFailure(status, code) {
  if (status === 0) return "network_retry";
  if (status === 502) return "capacity_retry";
  if (status === 503) return "service_retry";
  if (status === 403) return "auth_required";
  if (status === 429) return "rate_limited";
  if (status === 402) {
    return {
      daily_chat_limit: "daily_limit",
      visitor_daily_limit_reached: "daily_limit",
      concurrent_session_limit: "concurrent_limit",
      paid_credits_required_for_monetized_avatar: "paid_credits_required",
      sponsor_budget_exhausted: "sponsor_paused",
    }[code] || "payment_required";
  }
  return "fatal";
}

function newIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `ensemble-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function requestSessionOffer({
  endpoint = "/api/realtime/sessions",
  body,
  accessToken,
  idempotencyKey = newIdempotencyKey(),
  maxRetries = 10,
  retryDelayMs = 3000,
  concurrentLimitRetries = 3,
  concurrentLimitRetryDelayMs = 2000,
  onMetric,
  signal,
} = {}) {
  if (!body || typeof body !== "object") throw new TypeError("A session request body is required.");
  let lastError;
  let concurrentLimitAttempts = 0;
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    onMetric?.("session_request_started", { attempt: attempt + 1, maxAttempts: maxRetries + 1 });
    try {
      const headers = {
        "content-type": "application/json",
        "idempotency-key": idempotencyKey,
      };
      if (accessToken) headers.authorization = `Bearer ${accessToken}`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        credentials: "include",
        signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok) {
        onMetric?.("room_offer_received", { attempt: attempt + 1, status: payload.status || "ready" });
        return payload;
      }
      const code = sessionFailure(response.status, payload.error);
      const concurrentRetry = code === "concurrent_limit" && concurrentLimitAttempts < concurrentLimitRetries;
      if (concurrentRetry) concurrentLimitAttempts += 1;
      const retryable = [502, 503].includes(response.status) || concurrentRetry;
      lastError = new EnsembleSessionError(payload.message || `Session request failed: ${response.status}`, {
        status: response.status,
        code,
        body: payload,
        retryable,
      });
      if (!retryable || attempt >= maxRetries) throw lastError;
    } catch (error) {
      if (signal?.aborted) throw error;
      if (error instanceof EnsembleSessionError && !error.retryable) throw error;
      lastError = error instanceof EnsembleSessionError
        ? error
        : new EnsembleSessionError(String(error?.message || error), { code: "network_retry", retryable: true });
      if (attempt >= maxRetries) throw lastError;
    }
    onMetric?.("session_request_retry", { attempt: attempt + 1, code: lastError.code });
    await wait(lastError.code === "concurrent_limit" ? concurrentLimitRetryDelayMs : retryDelayMs);
  }
  throw lastError;
}

export async function requestSceneOffer({
  endpoint = "/api/realtime/scenes",
  characterIds,
  primaryCharacterId,
  durationSeconds = 300,
  accessToken,
  idempotencyKey = newIdempotencyKey(),
  maxRetries = 10,
  retryDelayMs = 3000,
  concurrentLimitRetries = 3,
  concurrentLimitRetryDelayMs = 2000,
  onMetric,
  signal,
} = {}) {
  if (!Array.isArray(characterIds) || characterIds.length < 2 || characterIds.length > 8) {
    throw new TypeError("A shared scene requires two to eight character ids.");
  }
  const cast = characterIds.map((value) => String(value || "").trim());
  if (cast.some((value) => !value || value.length > 160) || new Set(cast).size !== cast.length) {
    throw new TypeError("Shared-scene character ids must be non-empty and unique.");
  }
  const primary = String(primaryCharacterId || cast[0]).trim();
  if (!cast.includes(primary)) {
    throw new TypeError("The primary character must be part of the shared-scene cast.");
  }
  if (typeof durationSeconds === "boolean"
    || !Number.isSafeInteger(Number(durationSeconds))
    || Number(durationSeconds) < 1
    || Number(durationSeconds) > 600) {
    throw new TypeError("Shared-scene duration must be an integer from 1 to 600 seconds.");
  }
  return requestSessionOffer({
    endpoint,
    body: {
      characterIds: cast,
      primaryCharacterId: primary,
      durationSeconds: Number(durationSeconds),
    },
    accessToken,
    idempotencyKey,
    maxRetries,
    retryDelayMs,
    concurrentLimitRetries,
    concurrentLimitRetryDelayMs,
    onMetric,
    signal,
  });
}

const SCENE_CUE_FIELDS = new Set([
  "characterId", "emotion", "intensity", "engagement", "stance", "vocalization", "durationMs",
]);

function closedObject(value, allowed, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object.`);
  }
  if (Object.keys(value).some((field) => !allowed.has(field))) {
    throw new TypeError(`${label} contains unsupported fields.`);
  }
  return value;
}

function sceneCharacterId(value, label = "character") {
  const result = String(value || "").trim();
  if (!/^[A-Za-z0-9_.-]{1,160}$/.test(result)) throw new TypeError(`A valid ${label} id is required.`);
  return result;
}

function validatedSceneCues(cues, roster, speakerCharacterId) {
  if (!Array.isArray(cues) || cues.length !== roster.length) {
    throw new TypeError("A shared-scene turn requires exactly one cue for every admitted character.");
  }
  const seen = new Set();
  const result = cues.map((value) => {
    const cue = closedObject(value, SCENE_CUE_FIELDS, "Performance cue");
    const characterId = sceneCharacterId(cue.characterId, "cue character");
    if (!roster.includes(characterId) || seen.has(characterId)) {
      throw new TypeError("Performance cues must match the admitted cast exactly once.");
    }
    seen.add(characterId);
    const emotion = String(cue.emotion || "").trim();
    const engagement = String(cue.engagement || "").trim();
    const stance = String(cue.stance || "").trim();
    const vocalization = String(cue.vocalization || "").trim();
    if ([emotion, engagement, stance, vocalization].some((field) => !field || field.length > 128)) {
      throw new TypeError("Performance cue fields must contain 1 to 128 characters.");
    }
    const intensity = Number(cue.intensity);
    const durationMs = Number(cue.durationMs);
    if (!Number.isFinite(intensity) || intensity < 0 || intensity > 1) {
      throw new TypeError("Performance cue intensity must be from 0 to 1.");
    }
    if (!Number.isSafeInteger(durationMs) || durationMs < 80 || durationMs > 120000) {
      throw new TypeError("Performance cue duration must be an integer from 80 to 120000 milliseconds.");
    }
    if ((characterId === speakerCharacterId) !== (engagement === "Speaking")) {
      throw new TypeError("The selected speaker must be Speaking and every reactor must be non-speaking.");
    }
    return { characterId, emotion, intensity, engagement, stance, vocalization, durationMs };
  });
  if (seen.size !== roster.length) {
    throw new TypeError("Performance cues must match the admitted cast exactly once.");
  }
  return result;
}

async function postSceneInteraction({ endpoint, accessToken, body, signal }) {
  const headers = { "content-type": "application/json" };
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  let response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      credentials: "include",
      signal,
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new EnsembleSessionError(String(error?.message || error), {
      code: "scene_interaction_network_ambiguous",
      retryable: false,
    });
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new EnsembleSessionError(payload.message || `Shared-scene direction failed: ${response.status}`, {
      status: response.status,
      code: payload.error || "scene_interaction_failed",
      body: payload,
      retryable: false,
    });
  }
  return payload;
}

export async function pollSessionTicket({
  ticketId,
  endpoint,
  accessToken,
  maxWaitMs = 120000,
  defaultPollAfterMs = 3000,
  onMetric,
  signal,
} = {}) {
  if (!ticketId) throw new TypeError("A ticketId is required.");
  const ticketEndpoint = endpoint || `/api/realtime/tickets/${encodeURIComponent(ticketId)}`;
  const startedAt = Date.now();
  let polls = 0;
  while (Date.now() - startedAt <= maxWaitMs) {
    if (signal?.aborted) throw signal.reason || new DOMException("Ticket polling was aborted.", "AbortError");
    polls += 1;
    const headers = {};
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    let response;
    try {
      response = await fetch(ticketEndpoint, { method: "GET", headers, credentials: "include", signal });
    } catch (error) {
      onMetric?.("queue_poll_retry", { ticketId, polls, reason: "network" });
      await wait(defaultPollAfterMs);
      continue;
    }
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const code = sessionFailure(response.status, payload.error);
      if (![502, 503, 429].includes(response.status)) {
        throw new EnsembleSessionError(payload.message || `Queue poll failed: ${response.status}`, {
          status: response.status,
          code,
          body: payload,
          retryable: false,
        });
      }
      onMetric?.("queue_poll_retry", { ticketId, polls, status: response.status, code });
      const retryAfterSeconds = Number(response.headers.get("retry-after") || 0);
      await wait(retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : defaultPollAfterMs);
      continue;
    }
    if (payload.status === "ready") {
      onMetric?.("queue_ready", { ticketId, polls, waitedMs: Date.now() - startedAt });
      return payload;
    }
    if (payload.status !== "capacity_wait" || payload.ticketId !== ticketId) {
      throw new EnsembleSessionError("Queue poll returned an invalid offer.", {
        status: response.status,
        code: "invalid_ticket_offer",
        body: payload,
      });
    }
    onMetric?.("queue_position_updated", {
      ticketId,
      polls,
      position: payload.position || null,
      estimatedWaitSeconds: payload.estimatedWaitSeconds || null,
    });
    await wait(Number(payload.pollAfterMs || defaultPollAfterMs));
  }
  throw new EnsembleSessionError("Timed out waiting for realtime capacity.", {
    status: 408,
    code: "capacity_wait_timeout",
    retryable: false,
  });
}

function withTimeout(promise, milliseconds, message) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = window.setTimeout(() => reject(new Error(message)), milliseconds);
  });
  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timer));
}

export class EnsembleRealtimeRoom extends EventTarget {
  constructor({ videoElement, audioElement, onState, onMetric } = {}) {
    super();
    this.videoElement = videoElement || null;
    this.audioElement = audioElement || null;
    this.onState = onState || null;
    this.onMetric = onMetric || null;
    this.state = STATES.IDLE;
    this.history = [STATES.IDLE];
    this.room = null;
    this.offer = null;
    this.avatarVideo = null;
    this.avatarAudio = null;
    this.avatarTimer = null;
    this.audioSignalReceived = false;
    this.audioDetectionUnavailable = false;
    this.avatarReadiness = null;
    this.avatarAudioContext = null;
    this.avatarAudioContextResume = null;
    this.releaseStarted = false;
    this.pageCleanup = null;
    this.ending = false;
    this.capacityAbort = null;
    this.durationWarningTimer = null;
    this.durationEndTimer = null;
    this.durationDeadlineMs = null;
    this.storyCursor = null;
    this.avatarRoster = new Map();
  }

  metric(name, detail = {}) {
    const payload = { name, state: this.state, sessionId: this.offer?.sessionId || null, ...detail };
    this.dispatchEvent(new CustomEvent("metric", { detail: payload }));
    this.onMetric?.(name, payload);
  }

  transition(next, detail = {}) {
    if (!ALLOWED[this.state]?.includes(next)) {
      throw new Error(`Invalid Ensemble realtime transition: ${this.state} -> ${next}`);
    }
    this.state = next;
    this.history.push(next);
    const event = new CustomEvent("statechange", { detail: { state: next, history: [...this.history], ...detail } });
    this.dispatchEvent(event);
    this.onState?.(event.detail);
  }

  async connect(offer) {
    if (this.state !== STATES.IDLE) throw new Error("This room controller has already been used.");
    this.transition(STATES.REQUESTING);
    if (offer?.status === "capacity_wait") {
      if (!offer.ticketId || !offer.cancelUrl) {
        throw new Error("A capacity offer requires a ticketId and cancelUrl.");
      }
      this.offer = offer;
      this.installPageCleanup();
      this.transition(STATES.CAPACITY, {
        ticketId: offer.ticketId,
        position: offer.position || null,
        pollAfterMs: Number(offer.pollAfterMs || 3000),
      });
      return this.state;
    }
    return this.connectReadyOffer(offer);
  }

  async resume(offer) {
    if (this.state !== STATES.CAPACITY) throw new Error("Only a capacity-waiting room can accept a resumed offer.");
    if (offer?.status === "capacity_wait") {
      this.offer = { ...this.offer, ...offer };
      const detail = {
        ticketId: this.offer.ticketId,
        position: this.offer.position || null,
        pollAfterMs: Number(this.offer.pollAfterMs || 3000),
      };
      this.dispatchEvent(new CustomEvent("queuechange", { detail }));
      return this.state;
    }
    return this.connectReadyOffer(offer);
  }

  async waitForCapacity({ endpoint, accessToken, maxWaitMs, signal } = {}) {
    if (this.state !== STATES.CAPACITY) throw new Error("Only a capacity-waiting room can poll for a ready offer.");
    this.capacityAbort = new AbortController();
    const abortFromCaller = () => this.capacityAbort?.abort(signal?.reason);
    signal?.addEventListener("abort", abortFromCaller, { once: true });
    try {
      const ready = await pollSessionTicket({
        ticketId: this.offer.ticketId,
        endpoint: endpoint || this.offer.pollUrl,
        accessToken,
        maxWaitMs,
        defaultPollAfterMs: Number(this.offer.pollAfterMs || 3000),
        signal: this.capacityAbort.signal,
        onMetric: (name, detail) => {
          this.metric(name, detail);
          if (name === "queue_position_updated") {
            this.dispatchEvent(new CustomEvent("queuechange", { detail }));
          }
        },
      });
      return this.resume(ready);
    } finally {
      signal?.removeEventListener("abort", abortFromCaller);
      this.capacityAbort = null;
    }
  }

  async connectReadyOffer(offer) {
    if (offer?.status !== "ready" || !offer?.roomUrl || !offer?.userToken || !offer?.sessionId || !offer?.releaseUrl) {
      throw new Error("A ready offer requires server-issued roomUrl, userToken, sessionId, and releaseUrl values.");
    }
    this.offer = offer;
    const storySessionIds = Array.isArray(offer.storySessionIds)
      ? offer.storySessionIds
      : [offer.sessionId];
    if (!storySessionIds.includes(offer.sessionId)) {
      throw new Error("A ready Ensemble offer must include its primary session in storySessionIds.");
    }
    this.storyCursor = new StoryEnvelopeCursor(storySessionIds);
    const primaryPrefix = String(offer.avatarIdentityPrefix || "face-").trim();
    const avatarIdentityPrefixes = Array.isArray(offer.avatarIdentityPrefixes)
      ? offer.avatarIdentityPrefixes
      : [primaryPrefix];
    if (avatarIdentityPrefixes.length !== storySessionIds.length) {
      throw new Error("A ready Ensemble offer requires one avatar prefix per story session.");
    }
    if (Array.isArray(offer.characterIds) && offer.characterIds.length !== storySessionIds.length) {
      throw new Error("A ready Ensemble offer requires aligned character, session, and avatar rosters.");
    }
    if (Array.isArray(offer.characterIds)) {
      const characterIds = offer.characterIds.map((value) => String(value || "").trim()).filter(Boolean);
      if (new Set(characterIds).size !== storySessionIds.length
        || (offer.primaryCharacterId && !characterIds.includes(offer.primaryCharacterId))) {
        throw new Error("A ready Ensemble offer contains an invalid character roster.");
      }
      const primaryIndex = avatarIdentityPrefixes.indexOf(primaryPrefix);
      if (offer.primaryCharacterId && characterIds[primaryIndex] !== offer.primaryCharacterId) {
        throw new Error("A ready Ensemble offer misaligns its primary character and avatar prefix.");
      }
    }
    const characterIds = Array.isArray(offer.characterIds)
      ? offer.characterIds.map((value) => String(value || "").trim())
      : avatarIdentityPrefixes.map((_prefix, index) => index === avatarIdentityPrefixes.indexOf(primaryPrefix)
        ? String(offer.primaryCharacterId || "avatar")
        : `avatar-${index + 1}`);
    const renderers = Array.isArray(offer.renderers) ? offer.renderers : avatarIdentityPrefixes.map(() => null);
    if (renderers.length !== avatarIdentityPrefixes.length) {
      throw new Error("A ready Ensemble offer requires one renderer per avatar when renderers are supplied.");
    }
    this.avatarRoster = new Map(avatarIdentityPrefixes.map((prefix, index) => [prefix, Object.freeze({
      characterId: characterIds[index],
      storySessionId: storySessionIds[index],
      identityPrefix: prefix,
      renderer: renderers[index],
      primary: prefix === primaryPrefix,
    })]));
    this.avatarReadiness = new AvatarTrackReadiness(avatarIdentityPrefixes, primaryPrefix);
    // Credential/lease expiry may include cleanup grace. It must not extend the
    // admitted call duration advertised to the caller or the provider budget.
    this.durationDeadlineMs = deriveSessionDeadlineMs(offer);
    this.transition(STATES.CONNECTING);
    this.installPageCleanup();
    this.room = new Room({ adaptiveStream: true, dynacast: true, stopLocalTrackOnUnpublish: true });
    this.bindRoomEvents();

    const attempts = Number(offer.roomAttempts || 3);
    let lastError;
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        this.metric("room_join_attempt", { attempt, maxAttempts: attempts });
        await withTimeout(
          this.room.connect(offer.roomUrl, offer.userToken, { autoSubscribe: true }),
          Number(offer.roomTimeoutMs || 30000),
          "Timed out joining the Ensemble room.",
        );
        this.transition(STATES.WAITING, { attempt });
        this.metric("room_joined", { attempt });
        this.startAvatarJoinTimer();
        return this.state;
      } catch (error) {
        lastError = error;
        this.metric("room_join_failed", { attempt, error: String(error?.message || error) });
        if (attempt < attempts) await wait(Number(offer.roomRetryDelayMs || 2000));
      }
    }
    this.transition(STATES.ERROR, { error: String(lastError?.message || lastError) });
    throw lastError;
  }

  bindRoomEvents() {
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      const matchedPrefix = this.avatarReadiness?.match(participant.identity);
      if (!matchedPrefix) return;
      let admitted;
      try {
        admitted = this.avatarReadiness.recordTrack({
          prefix: matchedPrefix,
          participantIdentity: participant.identity,
          kind: track.kind,
          track,
        });
      } catch (error) {
        track.detach();
        this.metric("avatar_track_rejected", {
          participantIdentity: participant.identity,
          identityPrefix: matchedPrefix,
          kind: track.kind,
          error: String(error?.message || error),
        });
        return;
      }
      const primary = admitted.primary;
      const roster = this.avatarRoster.get(matchedPrefix) || {};
      this.dispatchEvent(new CustomEvent("avatartrack", { detail: {
        track,
        publication,
        participant,
        participantIdentity: participant.identity,
        identityPrefix: matchedPrefix,
        characterId: roster.characterId || null,
        storySessionId: roster.storySessionId || null,
        renderer: roster.renderer || null,
        primary,
      } }));
      if (track.kind === Track.Kind.Video) {
        if (primary) {
          this.avatarVideo = track;
          if (this.videoElement) this.attachMedia(track, this.videoElement, "video");
        }
        this.startAudioReadinessTimer(matchedPrefix);
        this.metric("avatar_video_received", { participantIdentity: participant.identity, primary });
      }
      if (track.kind === Track.Kind.Audio) {
        if (primary) {
          this.avatarAudio = track;
          if (this.audioElement) this.attachMedia(track, this.audioElement, "audio");
        }
        this.startAvatarAudioDetection(track, matchedPrefix);
        this.metric("avatar_audio_track_received", { participantIdentity: participant.identity, primary });
      }
      this.maybeReady();
    });
    this.room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      const removed = this.avatarReadiness?.removeTrack(track);
      if (!removed) return;
      const { prefix, kind, primary, record } = removed;
      const roster = this.avatarRoster.get(prefix) || {};
      this.dispatchEvent(new CustomEvent("avatartrackremoved", { detail: {
        track,
        publication,
        participant,
        participantIdentity: participant?.identity || null,
        identityPrefix: prefix,
        characterId: roster.characterId || null,
        storySessionId: roster.storySessionId || null,
        renderer: roster.renderer || null,
        kind,
        primary,
      } }));
      window.clearTimeout(record.audioTimer);
      record.audioTimer = null;
      if (primary && kind === "video") this.avatarVideo = null;
      if (primary && kind === "audio") {
        this.avatarAudio = null;
        this.audioSignalReceived = false;
        this.audioDetectionUnavailable = false;
      }
      if (kind === "audio") this.stopAvatarAudioDetection(prefix);
      track.detach();
      if (this.state === STATES.READY) {
        this.transition(STATES.RECONNECTING, { reason: "avatar_track_lost" });
        if (kind === "audio" && record.video) this.startAudioReadinessTimer(prefix);
      }
    });
    this.room.on(RoomEvent.Reconnecting, () => {
      if ([STATES.WAITING, STATES.AVATAR_JOINED, STATES.READY].includes(this.state)) {
        this.transition(STATES.RECONNECTING, { reason: "transport" });
        this.metric("room_reconnecting");
      }
    });
    this.room.on(RoomEvent.Reconnected, () => {
      if (this.state === STATES.RECONNECTING) {
        this.metric("room_reconnected");
        this.transition(STATES.WAITING, { recovered: true });
        this.maybeReady({ recovered: true });
      }
    });
    this.room.on(RoomEvent.Disconnected, () => {
      window.clearTimeout(this.avatarTimer);
      this.clearAvatarReadinessTimers();
      this.stopAvatarAudioDetection();
      this.closeAvatarAudioContext();
      if (!this.ending && ![STATES.ERROR, STATES.ENDED].includes(this.state)) {
        if (ALLOWED[this.state]?.includes(STATES.ERROR)) this.transition(STATES.ERROR, { error: "room_disconnected" });
      }
    });
    this.room.on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
      try {
        let message = decodeControlMessage(payload);
        if (STORY_TOPICS.has(topic)) {
          if (!this.storyCursor) throw new TypeError("Story cursor is not initialized for this room.");
          message = this.storyCursor.accept(message);
        }
        const detail = { topic, participantIdentity: participant?.identity || null, message };
        const eventName = {
          [TOPICS.TRANSCRIPT]: "transcript",
          [TOPICS.PERFORMANCE]: "performance",
          [TOPICS.WORLD_STATE]: "worldstate",
          [TOPICS.WORLD_EVENT]: "worldevent",
          [TOPICS.SAFETY]: "safety",
        }[topic] || "controlmessage";
        this.dispatchEvent(new CustomEvent(eventName, { detail }));
        this.metric("control_message_received", { topic: topic || null, eventName });
      } catch (error) {
        this.metric("control_message_rejected", { topic: topic || null, error: String(error?.message || error) });
      }
    });
    if (RoomEvent.TranscriptionReceived) {
      this.room.on(RoomEvent.TranscriptionReceived, (segments, participant, publication) => {
        const detail = {
          participantIdentity: participant?.identity || null,
          trackSid: publication?.trackSid || null,
          segments: (segments || []).map((segment) => ({
            id: segment.id,
            text: segment.text,
            final: Boolean(segment.final),
            startTime: Number.isFinite(segment.startTime) ? segment.startTime : null,
            endTime: Number.isFinite(segment.endTime) ? segment.endTime : null,
            firstReceivedTime: segment.firstReceivedTime || null,
            lastReceivedTime: segment.lastReceivedTime || null,
          })),
        };
        this.dispatchEvent(new CustomEvent("transcript", { detail }));
        this.metric("transcript_received", { segmentCount: detail.segments.length });
      });
    }
  }

  attachMedia(track, element, media) {
    track.attach(element);
    const playback = element.play?.();
    if (playback?.catch) {
      playback.catch((error) => {
        const detail = { media, error: String(error?.message || error) };
        this.metric("media_playback_blocked", detail);
        this.dispatchEvent(new CustomEvent("playbackblocked", { detail }));
      });
    }
  }

  async resumeMediaPlayback() {
    const elements = [this.videoElement, this.audioElement].filter(Boolean);
    const results = await Promise.allSettled(elements.map((element) => element.play?.()));
    const failed = results.filter((result) => result.status === "rejected");
    this.metric(failed.length ? "media_playback_retry_failed" : "media_playback_resumed", {
      attempted: elements.length,
      failed: failed.length,
    });
    if (failed.length) throw failed[0].reason;
  }

  maybeReady(detail = {}) {
    if (!this.avatarReadiness?.ready) return;
    this.clearAvatarReadinessTimers();
    window.clearTimeout(this.avatarTimer);
    if (this.state === STATES.CONNECTING) this.transition(STATES.WAITING);
    if ([STATES.WAITING, STATES.RECONNECTING].includes(this.state)) this.transition(STATES.AVATAR_JOINED);
    if (this.state === STATES.AVATAR_JOINED) {
      this.transition(STATES.READY, detail);
      const readiness = this.avatarReadiness.snapshot();
      this.metric("avatar_ready", {
        avatarCount: this.avatarReadiness.prefixes.length,
        nonSilentAvatarCount: Object.values(readiness).filter((item) => item.audioSignalReceived).length,
        audioFallbackAvatarCount: Object.values(readiness).filter((item) => item.audioDetectionUnavailable).length,
        audioReadiness: Object.values(readiness).every((item) => item.audioSignalReceived)
          ? "non_silent_signal"
          : "detector_unavailable_fallback",
      });
      this.startDurationTimers();
    }
  }

  startDurationTimers() {
    window.clearTimeout(this.durationWarningTimer);
    window.clearTimeout(this.durationEndTimer);
    if (!this.durationDeadlineMs) return;
    const remainingSeconds = Math.max(0, (this.durationDeadlineMs - Date.now()) / 1000);
    const warningSeconds = Math.min(Number(this.offer?.durationWarningSeconds || 60), Math.max(0, remainingSeconds - 1));
    if (warningSeconds > 0) {
      this.durationWarningTimer = window.setTimeout(() => {
        this.metric("session_duration_warning", { remainingSeconds: warningSeconds });
        this.dispatchEvent(new CustomEvent("durationwarning", { detail: { remainingSeconds: warningSeconds } }));
      }, Math.max(0, remainingSeconds - warningSeconds) * 1000);
    }
    this.durationEndTimer = window.setTimeout(() => {
      this.metric("session_duration_elapsed", { durationSeconds: Number(this.offer?.actualDurationSeconds || 0) });
      this.end().catch((error) => this.metric("session_duration_end_failed", { error: String(error?.message || error) }));
    }, remainingSeconds * 1000);
  }

  startAvatarJoinTimer() {
    window.clearTimeout(this.avatarTimer);
    this.avatarTimer = window.setTimeout(() => {
      const missing = this.avatarReadiness?.missing().filter((item) => item.kind === "video") || [];
      if (missing.length && ALLOWED[this.state]?.includes(STATES.ERROR)) {
        this.metric("avatar_join_timeout", { missingAvatarPrefixes: missing.map((item) => item.prefix) });
        this.transition(STATES.ERROR, {
          error: "avatar_join_timeout",
          missingAvatarPrefixes: missing.map((item) => item.prefix),
        });
      }
    }, Number(this.offer?.avatarJoinTimeoutMs || 30000));
  }

  startAudioReadinessTimer(prefix = this.avatarReadiness?.primaryPrefix) {
    const record = this.avatarReadiness?.require(prefix);
    if (!record) return;
    window.clearTimeout(record.audioTimer);
    record.audioTimer = window.setTimeout(() => {
      if ((!record.audio || (!record.audioSignalReceived && !record.audioDetectionUnavailable))
        && ALLOWED[this.state]?.includes(STATES.ERROR)) {
        this.metric("avatar_audio_timeout", { identityPrefix: prefix });
        this.transition(STATES.ERROR, { error: "avatar_audio_timeout", identityPrefix: prefix });
      }
    }, Number(this.offer?.avatarAudioTimeoutMs || 15000));
  }

  startAvatarAudioDetection(track, prefix = this.avatarReadiness?.primaryPrefix) {
    const record = this.avatarReadiness?.require(prefix);
    if (!record) return;
    this.stopAvatarAudioDetection(prefix);
    if (this.offer?.audioReadinessMode === "track_present") {
      this.avatarReadiness.markDetectionUnavailable(prefix);
      if (prefix === this.avatarReadiness.primaryPrefix) this.audioDetectionUnavailable = true;
      this.metric("avatar_audio_detection_unavailable", {
        identityPrefix: prefix,
        reason: "offer_track_present_mode",
      });
      this.maybeReady();
      return;
    }
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const mediaTrack = track?.mediaStreamTrack;
    if (!AudioContextClass || !mediaTrack || typeof MediaStream !== "function") {
      this.markAudioDetectionUnavailable(prefix, "web_audio_unavailable");
      return;
    }
    try {
      const context = this.avatarAudioContext && this.avatarAudioContext.state !== "closed"
        ? this.avatarAudioContext
        : new AudioContextClass();
      this.avatarAudioContext = context;
      const source = context.createMediaStreamSource(new MediaStream([mediaTrack]));
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const samples = new Float32Array(analyser.fftSize);
      const threshold = Number(this.offer?.avatarAudioSilenceThreshold || 0.02);
      const tick = () => {
        if (!record.audioDetection || record.audioDetection.context !== context) return;
        analyser.getFloatTimeDomainData(samples);
        const energy = Math.sqrt(samples.reduce((sum, sample) => sum + sample * sample, 0) / samples.length);
        if (energy >= threshold) {
          this.avatarReadiness.markAudioSignal(prefix);
          if (prefix === this.avatarReadiness.primaryPrefix) this.audioSignalReceived = true;
          this.metric("avatar_audio_signal_received", { identityPrefix: prefix, energy, threshold });
          this.stopAvatarAudioDetection(prefix);
          this.maybeReady();
          return;
        }
        record.audioDetection.frame = window.requestAnimationFrame(tick);
      };
      record.audioDetection = { context, source, analyser, frame: window.requestAnimationFrame(tick) };
      if (context.state === "suspended" && !this.avatarAudioContextResume) {
        this.avatarAudioContextResume = context.resume().catch((error) => {
          if (this.avatarAudioContext !== context || this.offer?.allowAudioDetectionFallback === false) return;
          for (const exactPrefix of this.avatarReadiness.prefixes) {
            const exactRecord = this.avatarReadiness.require(exactPrefix);
            if (!exactRecord.audio || exactRecord.audioSignalReceived || exactRecord.audioDetectionUnavailable) continue;
            this.stopAvatarAudioDetection(exactPrefix);
            this.avatarReadiness.markDetectionUnavailable(exactPrefix);
            if (exactPrefix === this.avatarReadiness.primaryPrefix) this.audioDetectionUnavailable = true;
            this.metric("avatar_audio_detection_unavailable", {
              identityPrefix: exactPrefix,
              reason: `audio_context_resume_failed:${String(error?.message || error)}`,
            });
          }
          this.maybeReady();
        }).finally(() => {
          if (this.avatarAudioContext === context) this.avatarAudioContextResume = null;
        });
      }
    } catch (error) {
      this.markAudioDetectionUnavailable(prefix, String(error?.message || error));
    }
  }

  markAudioDetectionUnavailable(prefix = this.avatarReadiness?.primaryPrefix, reason) {
    if (this.offer?.allowAudioDetectionFallback === false) return;
    this.avatarReadiness.markDetectionUnavailable(prefix);
    if (prefix === this.avatarReadiness.primaryPrefix) this.audioDetectionUnavailable = true;
    this.metric("avatar_audio_detection_unavailable", { identityPrefix: prefix, reason });
    this.maybeReady();
  }

  stopAvatarAudioDetection(prefix) {
    const prefixes = prefix ? [prefix] : (this.avatarReadiness?.prefixes || []);
    for (const exactPrefix of prefixes) {
      const record = this.avatarReadiness.require(exactPrefix);
      const detection = record.audioDetection;
      record.audioDetection = null;
      if (!detection) continue;
      window.cancelAnimationFrame(detection.frame);
      try { detection.source.disconnect(); } catch (_) { /* already disconnected */ }
      try { detection.analyser.disconnect(); } catch (_) { /* already disconnected */ }
    }
  }

  closeAvatarAudioContext() {
    const context = this.avatarAudioContext;
    this.avatarAudioContext = null;
    this.avatarAudioContextResume = null;
    if (context && context.state !== "closed") context.close().catch(() => {});
  }

  clearAvatarReadinessTimers() {
    for (const prefix of this.avatarReadiness?.prefixes || []) {
      const record = this.avatarReadiness.require(prefix);
      window.clearTimeout(record.audioTimer);
      record.audioTimer = null;
    }
  }

  async enableMicrophone() {
    if (!this.room) throw new Error("Join a room before enabling the microphone.");
    try {
      await this.room.localParticipant.setMicrophoneEnabled(true, {
        autoGainControl: true,
        noiseSuppression: true,
        echoCancellation: true,
      });
      this.metric("microphone_enabled");
    } catch (error) {
      this.metric("media_permission_denied", { media: "microphone", error: String(error?.message || error) });
      throw error;
    }
  }

  async enableCamera() {
    if (!this.room) throw new Error("Join a room before enabling the camera.");
    try {
      await this.room.localParticipant.setCameraEnabled(true, {
        resolution: { width: 500, height: 500, frameRate: 24 },
        facingMode: "user",
      });
      this.metric("camera_enabled");
    } catch (error) {
      this.metric("media_permission_denied", { media: "camera", error: String(error?.message || error) });
      throw error;
    }
  }

  async sendText(text, { turnId } = {}) {
    const value = String(text || "").trim().slice(0, 4000);
    if (!value || !this.room) return;
    if (turnId) {
      await this.publishControl(TOPICS.CHAT, { type: "chat", turnId, text: value });
      return;
    }
    if (typeof this.room.localParticipant.sendText === "function") {
      await this.room.localParticipant.sendText(value, { topic: TOPICS.CHAT });
      return;
    }
    const data = new TextEncoder().encode(JSON.stringify({ type: "chat", text: value }));
    await this.room.localParticipant.publishData(data, { reliable: true, topic: TOPICS.CHAT });
  }

  async sendPerformanceIntent({
    turnId = newIdempotencyKey(),
    emotion,
    intensity = 0.5,
    engagement = "Speaking",
    stance = "Animation.Stance.OpenStand",
    vocalization = "none",
  } = {}) {
    if (!this.room) throw new Error("Join a room before sending a performance intent.");
    const value = {
      type: "performance_intent",
      turnId: String(turnId).slice(0, 128),
      emotion: String(emotion || "Interested").slice(0, 128),
      intensity: Math.max(0, Math.min(1, Number(intensity) || 0)),
      engagement: String(engagement || "Speaking").slice(0, 128),
      stance: String(stance || "Animation.Stance.OpenStand").slice(0, 128),
      vocalization: String(vocalization || "none").slice(0, 128),
    };
    await this.publishControl(TOPICS.PERFORMANCE, value);
    this.metric("performance_intent_sent", {
      turnId: value.turnId,
      emotion: value.emotion,
      intensity: value.intensity,
      engagement: value.engagement,
      stance: value.stance,
    });
    return value.turnId;
  }

  async sendTurn(text, performance = {}) {
    if (!this.room) throw new Error("Join a room before sending a story turn.");
    if (this.offer?.sceneRunId) {
      throw new Error("Use sendSceneConversation() so the target character authors the response in a shared scene.");
    }
    const value = String(text || "").trim().slice(0, 4000);
    if (!value) return null;
    const turnId = String(performance.turnId || newIdempotencyKey()).slice(0, 128);
    await this.sendPerformanceIntent({ ...performance, turnId });
    await this.sendText(value, { turnId });
    this.metric("story_turn_sent", { turnId });
    return turnId;
  }

  async sendSceneConversation({
    targetCharacterId,
    text,
    requestId = newIdempotencyKey(),
    endpoint,
    accessToken,
    signal,
  } = {}) {
    if (!this.room) throw new Error("Join a room before talking to a shared-scene character.");
    const sceneRunId = String(this.offer?.sceneRunId || "").trim();
    if (!sceneRunId) throw new Error("This room is not an admitted shared scene.");
    const roster = [...this.avatarRoster.values()].map((item) => item.characterId);
    if (roster.length < 2 || roster.length > 8 || new Set(roster).size !== roster.length) {
      throw new Error("This shared scene does not have one aligned two-to-eight character roster.");
    }
    const target = sceneCharacterId(targetCharacterId, "target character");
    if (!roster.includes(target)) throw new TypeError("The target character is not in this shared scene.");
    const visitorText = String(text || "").trim();
    if (!visitorText || visitorText.length > 4000) {
      throw new TypeError("Visitor dialogue must contain between 1 and 4000 characters.");
    }
    const request = sceneCharacterId(requestId, "conversation request");
    const result = await postSceneInteraction({
      endpoint: endpoint || `/api/realtime/scenes/${encodeURIComponent(sceneRunId)}/conversation`,
      accessToken,
      body: { requestId: request, targetCharacterId: target, text: visitorText },
      signal,
    });
    this.metric(
      result.status === "safety_intercept" ? "scene_conversation_safety_intercept" : "scene_conversation_enqueued",
      {
        requestId: request,
        sceneTurnId: result.sceneTurnId || null,
        targetCharacterId: target,
        actorCount: roster.length,
      },
    );
    return result;
  }

  async sendSceneTurn({
    speakerCharacterId,
    text,
    cues,
    endpoint,
    accessToken,
    signal,
  } = {}) {
    if (!this.room) throw new Error("Join a room before directing a shared-scene turn.");
    const sceneRunId = String(this.offer?.sceneRunId || "").trim();
    if (!sceneRunId) throw new Error("This room is not an admitted shared scene.");
    const roster = [...this.avatarRoster.values()].map((item) => item.characterId);
    if (roster.length < 2 || roster.length > 8 || new Set(roster).size !== roster.length) {
      throw new Error("This shared scene does not have one aligned two-to-eight character roster.");
    }
    const speaker = sceneCharacterId(speakerCharacterId, "speaker character");
    if (!roster.includes(speaker)) throw new TypeError("The selected speaker is not in this shared scene.");
    const dialogue = String(text || "").trim();
    if (!dialogue || dialogue.length > 4000) {
      throw new TypeError("Shared-scene dialogue must contain between 1 and 4000 characters.");
    }
    const body = { speakerCharacterId: speaker, text: dialogue };
    if (cues !== undefined) body.cues = validatedSceneCues(cues, roster, speaker);
    const result = await postSceneInteraction({
      endpoint: endpoint || `/api/realtime/scenes/${encodeURIComponent(sceneRunId)}/turn`,
      accessToken,
      body,
      signal,
    });
    this.metric(result.status === "safety_intercept" ? "scene_turn_safety_intercept" : "scene_turn_planned", {
      sceneTurnId: result.sceneTurnId || null,
      speakerCharacterId: speaker,
      actorCount: roster.length,
    });
    return result;
  }

  async sendWorldAction(action, payload = {}) {
    if (!this.room) throw new Error("Join a room before sending a world action.");
    if (this.offer?.sceneRunId) {
      throw new Error("Use sendSceneWorldAction() so the server can synchronize the shared world.");
    }
    const name = String(action || "");
    if (!/^[A-Za-z0-9_.-]{1,128}$/.test(name)) throw new TypeError("A valid world action name is required.");
    await this.publishControl(TOPICS.WORLD_ACTION, { type: "world_action", action: name, payload });
    this.metric("world_action_sent", { action: name });
  }

  async sendSceneWorldAction({
    actorCharacterId,
    action,
    payload,
    endpoint,
    accessToken,
    signal,
  } = {}) {
    if (!this.room) throw new Error("Join a room before sending a shared-world action.");
    const sceneRunId = String(this.offer?.sceneRunId || "").trim();
    if (!sceneRunId) throw new Error("This room is not an admitted shared scene.");
    const roster = [...this.avatarRoster.values()].map((item) => item.characterId);
    const actor = sceneCharacterId(actorCharacterId, "actor character");
    if (!roster.includes(actor)) throw new TypeError("The acting character is not in this shared scene.");
    const name = String(action || "").trim();
    const allowedPayload = name === "claim_prop"
      ? new Set(["prop_id"])
      : (name === "resolve_fire" ? new Set(["event_id", "extinguisher_id"]) : null);
    if (!allowedPayload) throw new TypeError("Shared-world action is invalid.");
    const actionPayload = closedObject(payload, allowedPayload, "World-action payload");
    if (Object.keys(actionPayload).length !== allowedPayload.size
      || [...allowedPayload].some((field) => !Object.hasOwn(actionPayload, field))) {
      throw new TypeError("World-action payload is missing required fields.");
    }
    const normalizedPayload = Object.fromEntries(Object.entries(actionPayload).map(([key, value]) => [
      key,
      sceneCharacterId(value, key.replaceAll("_", " ")),
    ]));
    const result = await postSceneInteraction({
      endpoint: endpoint || `/api/realtime/scenes/${encodeURIComponent(sceneRunId)}/world-actions`,
      accessToken,
      body: { actorCharacterId: actor, action: name, payload: normalizedPayload },
      signal,
    });
    this.metric("scene_world_action_applied", {
      actorCharacterId: actor,
      action: name,
      worldRevision: result.worldRevision,
    });
    return result;
  }

  async publishControl(topic, value) {
    const data = new TextEncoder().encode(JSON.stringify(value));
    if (data.byteLength > MAX_CONTROL_MESSAGE_BYTES) throw new RangeError("Realtime control data exceeds 64 KiB.");
    await this.room.localParticipant.publishData(data, { reliable: true, topic });
  }

  async interrupt() {
    if (!this.room) throw new Error("Join a room before interrupting.");
    if (this.offer?.sceneRunId) {
      throw new Error("Use interruptScene() so the server interrupts the authoritative shared-scene speaker.");
    }
    const data = new TextEncoder().encode(JSON.stringify({ sessionId: this.offer?.sessionId }));
    await this.room.localParticipant.publishData(data, { reliable: true, topic: TOPICS.INTERRUPT });
    this.metric("performance_interrupted");
  }

  async interruptScene({ atMs = 0, endpoint, accessToken, signal } = {}) {
    if (!this.room) throw new Error("Join a room before interrupting a shared scene.");
    const sceneRunId = String(this.offer?.sceneRunId || "").trim();
    if (!sceneRunId) throw new Error("This room is not an admitted shared scene.");
    const offset = Number(atMs);
    if (!Number.isSafeInteger(offset) || offset < 0 || offset > 120000) {
      throw new TypeError("Shared-scene interrupt time must be an integer from 0 to 120000 milliseconds.");
    }
    const result = await postSceneInteraction({
      endpoint: endpoint || `/api/realtime/scenes/${encodeURIComponent(sceneRunId)}/interrupt`,
      accessToken,
      body: { atMs: offset },
      signal,
    });
    this.metric("scene_performance_interrupted", {
      speakerCharacterId: result.speakerCharacterId || null,
      atMs: offset,
    });
    return result;
  }

  installPageCleanup() {
    if (this.pageCleanup) return;
    this.pageCleanup = () => {
      this.ending = true;
      this.releaseSession({ throwOnError: false });
      this.room?.disconnect().catch(() => {});
    };
    window.addEventListener("pagehide", this.pageCleanup);
    window.addEventListener("beforeunload", this.pageCleanup);
  }

  removePageCleanup() {
    if (!this.pageCleanup) return;
    window.removeEventListener("pagehide", this.pageCleanup);
    window.removeEventListener("beforeunload", this.pageCleanup);
    this.pageCleanup = null;
  }

  async releaseSession({ throwOnError = true } = {}) {
    const releaseUrl = this.offer?.releaseUrl || this.offer?.cancelUrl || this.offer?.forceEndUrl;
    if (!releaseUrl || this.releaseStarted) return;
    this.releaseStarted = true;
    const kind = this.offer?.cancelUrl && !this.offer?.releaseUrl ? "capacity_ticket" : "session";
    const method = this.offer?.releaseMethod
      || this.offer?.cancelMethod
      || (this.offer?.releaseUrl || this.offer?.cancelUrl ? "POST" : "DELETE");
    this.metric("session_release_started", { method, kind });
    try {
      const headers = {};
      if (kind === "session" && this.offer?.sessionProof) {
        headers["x-ensemble-session-proof"] = this.offer.sessionProof;
      }
      const response = await fetch(releaseUrl, {
        method,
        headers,
        keepalive: true,
        credentials: "include",
      });
      if (!response.ok && ![404, 409].includes(response.status)) {
        throw new Error(`Server teardown failed: ${response.status}`);
      }
      this.metric("session_released", { status: response.status, kind });
    } catch (error) {
      this.metric("session_release_failed", { error: String(error?.message || error) });
      if (throwOnError) throw error;
    }
  }

  async end() {
    if ([STATES.ENDED, STATES.ENDING].includes(this.state)) return;
    if (!ALLOWED[this.state]?.includes(STATES.ENDING)) {
      if (this.state === STATES.ERROR) this.transition(STATES.ENDING);
      else throw new Error(`Cannot end a room from ${this.state}.`);
    } else this.transition(STATES.ENDING);
    this.ending = true;
    this.capacityAbort?.abort();
    window.clearTimeout(this.avatarTimer);
    window.clearTimeout(this.durationWarningTimer);
    window.clearTimeout(this.durationEndTimer);
    this.clearAvatarReadinessTimers();
    this.stopAvatarAudioDetection();
    this.closeAvatarAudioContext();
    for (const track of new Set(this.avatarReadiness?.tracks() || [this.avatarVideo, this.avatarAudio])) {
      track?.detach();
    }
    await this.room?.disconnect();
    try {
      await this.releaseSession();
    } catch (error) {
      this.transition(STATES.ERROR, { error: String(error?.message || error) });
      throw error;
    }
    this.removePageCleanup();
    this.transition(STATES.ENDED);
  }
}

export { STATES, TOPICS };
